import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

import type { EntityId } from './types'

/**
 * The page side of an "Expand / Collapse" checkbox over a table with expandable rows.
 * Expansion is a view preference of one table only — no store behind it. The checkbox
 * states the intent and the table, which is the side that knows the rows, reports back
 * which ones are open.
 */
export function useExpandedRows() {
  const expandedIds = ref<string[]>([])
  const allExpanded = ref(false)

  function setExpanded(ids: string[]) {
    expandedIds.value = ids
  }

  return { expandedIds, allExpanded, setExpanded }
}

/**
 * The table side of the same checkbox: turns the page's intent into the row ids to open,
 * and reports every change — the checkbox's and the user's own clicks alike — back through
 * `onChange`. Bind the returned `onExpandChange` to the table's `expand-change`.
 */
export function useExpandAllRows<T extends { id: EntityId }>(options: {
  items: MaybeRefOrGetter<T[]>
  expandAll: MaybeRefOrGetter<boolean>
  expandedIds: MaybeRefOrGetter<string[]>
  onChange: (ids: string[]) => void
}) {
  watch(
    () => [toValue(options.expandAll), toValue(options.items)] as const,
    ([expandAll, rows]) => {
      if (!expandAll) return

      const ids = rows.map((item) => String(item.id))
      if (ids.length !== toValue(options.expandedIds).length)
        options.onChange(ids)
    }
  )

  watch(
    () => toValue(options.expandAll),
    (expandAll) => {
      if (!expandAll) options.onChange([])
    }
  )

  function onExpandChange(_row: T, expandedRows: T[]) {
    options.onChange(expandedRows.map((item) => String(item.id)))
  }

  return { onExpandChange }
}
